/**
 * Positive-event microinteractions: level-up pop, evolution flourish, pickup tick,
 * surge tier-up, boss defeat.
 *
 * Every animation finishes inside 400 ms. With reduced motion on (the §13.1.3
 * "Reduced flashes" toggle, or the OS `prefers-reduced-motion` query) the banner
 * still appears — the information is never motion-only — but transforms, scale pops
 * and the flash layer are all suppressed (§11.8 photosafety).
 */

import { div, setText } from './dom';
import { formatInt } from './format';

export type MicroKind = 'levelup' | 'evolution' | 'pickup' | 'surge' | 'boss';

const POOL_SIZE = 6;
const BANNER_MS = 380;
const TICK_MS = 220;

interface Banner {
  root: HTMLElement;
  title: HTMLElement;
  sub: HTMLElement;
  busyUntil: number;
  timer: number;
}

export class Microinteractions {
  readonly root: HTMLElement;
  private banners: Banner[] = [];
  private cursor = 0;
  private tickLayer: HTMLElement;
  private tickTimer = 0;
  private reduced = false;

  constructor() {
    this.root = div('micro-layer');
    for (let i = 0; i < POOL_SIZE; i++) {
      const b = div('micro-banner');
      const title = div('micro-title', '');
      const sub = div('micro-sub', '');
      b.appendChild(title);
      b.appendChild(sub);
      b.hidden = true;
      this.root.appendChild(b);
      this.banners.push({ root: b, title, sub, busyUntil: 0, timer: 0 });
    }
    this.tickLayer = div('micro-tick');
    this.tickLayer.hidden = true;
    this.root.appendChild(this.tickLayer);
  }

  setReducedMotion(on: boolean): void {
    this.reduced = on;
    this.root.classList.toggle('reduced', on);
  }

  get reducedMotion(): boolean {
    return this.reduced;
  }

  levelUp(level: number): void {
    this.show('levelup', 'LEVEL ' + formatInt(level), 'Choose an upgrade');
  }

  evolution(displayName: string): void {
    this.show('evolution', 'EVOLVED', displayName);
  }

  surgeUp(tier: number): void {
    this.show('surge', '×' + formatInt(tier), 'Lumensurge rising');
  }

  bossDefeated(displayName: string): void {
    this.show('boss', 'DOWN', displayName);
  }

  /** Sub-frame acknowledgement for mote pickup; cheapest of the set. */
  pickupTick(): void {
    if (this.reduced) return;
    this.tickLayer.hidden = false;
    this.tickLayer.classList.remove('is-on');
    // Force a reflow so a rapid second pickup restarts the animation.
    void this.tickLayer.offsetWidth;
    this.tickLayer.classList.add('is-on');
    window.clearTimeout(this.tickTimer);
    this.tickTimer = window.setTimeout(() => {
      this.tickLayer.classList.remove('is-on');
      this.tickLayer.hidden = true;
    }, TICK_MS);
  }

  private show(kind: MicroKind, title: string, sub: string): void {
    const b = this.banners[this.cursor];
    this.cursor = (this.cursor + 1) % POOL_SIZE;
    window.clearTimeout(b.timer);
    b.root.className = 'micro-banner kind-' + kind;
    setText(b.title, title);
    setText(b.sub, sub);
    b.root.hidden = false;
    b.root.classList.remove('is-in');
    void b.root.offsetWidth;
    b.root.classList.add('is-in');
    b.timer = window.setTimeout(() => {
      b.root.classList.remove('is-in');
      b.root.hidden = true;
    }, BANNER_MS);
  }

  /** Clears everything in flight — called on screen change and run reset. */
  clear(): void {
    for (let i = 0; i < this.banners.length; i++) {
      const b = this.banners[i];
      window.clearTimeout(b.timer);
      b.root.classList.remove('is-in');
      b.root.hidden = true;
    }
    window.clearTimeout(this.tickTimer);
    this.tickLayer.classList.remove('is-on');
    this.tickLayer.hidden = true;
  }
}
